/**
 * LandingPage — The public-facing marketing page for Connekted.in
 *
 * Design: Ported 1:1 from the locked-in connektedin.html
 * Tweaks applied:
 *   1. Section eyebrow dividers removed (no "SIX FEATURES. ZERO CLUTTER." headings)
 *   2. "Phase 1 free for 10,000 professionals" hero badge removed
 *   3. Nav: no Q-Art link; Q-Art not mentioned anywhere (backend feature)
 *   4. About Bite Binary → external link to https://bitebinary.com/#about
 *   5. Legal pages link to internal routes (Privacy, Terms, Cookie Policy, Security)
 *   6. Footer: Q-Art Codes and Roadmap removed from Product column
 *   7. Problem section rewritten: founder narrative (EU summit, lost business cards)
 *   8. Founder quote rewritten: no LinkedIn mention
 *   9. Quote mark positioned on left (open quote)
 *
 * API_TODO: Replace "#cta" anchors with actual signup route once auth is wired.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import AscendingK from '../components/AscendingK';
import { Icons } from '../components/Icons';
import { apiRequest } from '../lib/api';
import { getPublicBaseUrl, getPublicCardUrl } from '../lib/site';
import '../styles/landing.css';

/**
 * LiveCounter — Animated count-up showing total users on the platform
 * API_TODO: GET /api/stats/users → { count: number }
 * Replace MOCK_COUNT with real data from the backend.
 */
function LiveCounter() {
  const [count, setCount] = useState(0);
  const [targetCount, setTargetCount] = useState(2847);
  const [hasAnimated, setHasAnimated] = useState(false);
  const counterRef = useRef(null);

  useEffect(() => {
    apiRequest('/stats/users', { token: null })
      .then((data) => setTargetCount(data.count || 2847))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const el = counterRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          // Animate count from 0 to target
          const duration = 2000; // ms
          const start = performance.now();
          const animate = (now) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            // Ease-out cubic
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * targetCount));
            if (progress < 1) requestAnimationFrame(animate);
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasAnimated, targetCount]);

  return (
    <div className="hero-live-counter" ref={counterRef}>
      <span className="hero-counter-pulse"><span className="pulse" /></span>
      <span className="hero-counter-text">
        Trusted by <strong className="hero-counter-num serif">{count.toLocaleString()}</strong> professionals and counting
      </span>
    </div>
  );
}

export default function LandingPage() {
  const heroVisualRef = useRef(null);

  // Parallax on hero floating cards (desktop only)
  useEffect(() => {
    const hero = heroVisualRef.current;
    if (!hero || !window.matchMedia('(min-width: 1100px)').matches) return;

    const onMove = (e) => {
      const rect = hero.getBoundingClientRect();
      const x = (e.clientX - rect.left - rect.width / 2) / rect.width;
      const y = (e.clientY - rect.top - rect.height / 2) / rect.height;
      hero.querySelectorAll('.floating-card').forEach((card, i) => {
        const intensity = (i + 1) * 8;
        const base = card.classList.contains('card-main') ? -4
          : card.classList.contains('card-wallet') ? 7 : -6;
        const translate = card.classList.contains('card-main') ? 'translateX(-55%) ' : '';
        card.style.transform = `${translate}rotate(${base}deg) translate(${x * intensity}px, ${y * intensity}px)`;
      });
    };
    const onLeave = () => {
      hero.querySelectorAll('.floating-card').forEach(card => { card.style.transform = ''; });
    };

    hero.addEventListener('mousemove', onMove);
    hero.addEventListener('mouseleave', onLeave);
    return () => {
      hero.removeEventListener('mousemove', onMove);
      hero.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  // IntersectionObserver scroll reveal
  useEffect(() => {
    const targets = document.querySelectorAll('.stat-card, .feature, .step, .qr-wrap, .quote-wrap');
    targets.forEach(el => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(24px)';
      el.style.transition = 'opacity 0.7s cubic-bezier(.2,.7,.2,1), transform 0.7s cubic-bezier(.2,.7,.2,1)';
    });

    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry, i) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateY(0)';
          }, i * 60);
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });

    targets.forEach(t => io.observe(t));
    return () => io.disconnect();
  }, []);

  return (
    <>
      {/* ═══════ NAV ═══════ */}
      <nav className="nav">
        <div className="nav-inner">
          <Link to="/" className="nav-logo">
            <AscendingK size={18} color="#0A2540" />
            <span className="nav-wordmark">Connekted <em>in</em></span>
          </Link>
          <ul className="nav-links">
            <li><a href="#features">Features</a></li>
            <li><a href="#how">How it works</a></li>
            <li><a href="#story">Our story</a></li>
          </ul>
          <Link to="/auth" className="nav-cta">Get your card &rarr;</Link>
        </div>
      </nav>

      {/* ═══════ HERO ═══════ */}
      <section className="hero">
        <div className="container">
          <div className="hero-grid">
            <div>
              {/* TWEAK #2: Badge removed */}
              <h1>Your business card, <em>reimagined</em> for the post-paper era.</h1>
              <p className="hero-sub">
                A digital business card that lives in your wallet, scans into a CRM, and quietly builds your network — while you focus on the conversation.
              </p>
              <div className="hero-actions">
                <Link to="/auth" className="btn-primary">
                  Create your card free
                  <span className="arrow">&rarr;</span>
                </Link>
                <a href="#how" className="btn-ghost">See how it works</a>
              </div>
              <div className="hero-meta">
                <div className="stat">
                  <div className="num">&lt; 2 min</div>
                  <div className="lbl">To your first card</div>
                </div>
                <div className="stat">
                  <div className="num">$0</div>
                  <div className="lbl">Free to use</div>
                </div>
                <div className="stat">
                  <div className="num">6 features</div>
                  <div className="lbl">No upsell, no app store</div>
                </div>
              </div>
              <LiveCounter />
            </div>

            {/* Floating card stack */}
            <div className="hero-visual" ref={heroVisualRef}>
              <div className="hero-blob"></div>

              <div className="floating-card card-notif">
                <div className="notif-row">
                  <div className="notif-ava">M</div>
                  <div className="notif-text">
                    <strong>Maria Conti scanned your card</strong>
                    <span>AI Week Milan &middot; 2 min ago</span>
                  </div>
                </div>
                <div className="notif-tag">Saved to Hot Leads</div>
              </div>

              <div className="floating-card card-main">
                <div className="avatar">IE</div>
                <div className="name">Iwin Ettumanukaran</div>
                <div className="role">Co-Founder &amp; CEO &middot; Bite Binary</div>
                <div className="qr-block">
                  <div className="qr-vis">
                    <div className="dots"></div>
                    <div className="center-k"><AscendingK size={10} color="#0A2540" /></div>
                  </div>
                  <div className="qr-meta">
                    <strong>{getPublicBaseUrl().replace(/^https?:\/\//, '')}</strong>
                    /iwin
                  </div>
                </div>
              </div>

              <div className="floating-card card-wallet">
                <div className="wallet-head">
                  <div className="wallet-brand">Connekted.in</div>
                  <div className="wallet-icon"></div>
                </div>
                <div className="wallet-row">Business Card</div>
                <div className="wallet-name">Iwin E.</div>
                <div className="wallet-divider"></div>
                <div className="wallet-foot">
                  <div>
                    <div className="wallet-row">Tap to share</div>
                    <div style={{ fontFamily: "'Fraunces',serif", fontSize: 14, marginTop: 4 }}>{getPublicCardUrl('iwin').replace(/^https?:\/\//, '')}</div>
                  </div>
                  <div className="wallet-mini-qr"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ MARQUEE ═══════ */}
      <div className="marquee">
        <div className="marquee-label">Built for professional summits, networking events &amp; conferences worldwide</div>
        <div className="marquee-track">
          {['AI Week Milan', 'SaaStr Annual', 'Web Summit', 'TNW Conference', 'Slush Helsinki', 'Collision', 'South by Southwest',
            'AI Week Milan', 'SaaStr Annual', 'Web Summit', 'TNW Conference', 'Slush Helsinki', 'Collision', 'South by Southwest'
          ].map((name, i) => (
            <div className="marquee-item" key={i}>{name}</div>
          ))}
        </div>
      </div>

      {/* ═══════ PROBLEM / STATS — TWEAK #8 & #9: Rewritten narrative ═══════ */}
      <section className="problem" id="story">
        <div className="container">
          {/* TWEAK #1: No section eyebrow */}
          <h2 className="section-title">
            We kept losing the people we met. <em>So we built something about it.</em>
          </h2>
          <p className="section-sub">
            At a startup summit in the EU, our founder exchanged dozens of business cards in a single afternoon. By the flight home, half were crumpled in a jacket pocket and the names had already blurred together. The conversations were great &mdash; but the follow-ups never happened. That moment became Connekted.in.
          </p>

          {/* Stats with source citations kept */}
          <div className="stats-grid">
            <div className="stat-card s1">
              <div className="stat-icon">{Icons.warning}</div>
              <div className="stat-num">88<span style={{ fontSize: 32, color: 'var(--muted)' }}>%</span></div>
              <div className="stat-label">Of paper business cards are thrown away within a week of being received.</div>
            </div>
            <div className="stat-card s2">
              <div className="stat-icon">{Icons.trendDown}</div>
              <div className="stat-num">10B+</div>
              <div className="stat-label">Paper business cards printed every year worldwide &mdash; most ending up in landfill.</div>
            </div>
            <div className="stat-card s3">
              <div className="stat-icon">{Icons.lock}</div>
              <div className="stat-num">72<span style={{ fontSize: 32, color: 'var(--muted)' }}>%</span></div>
              <div className="stat-label">Of professionals say they judge a company by the quality of its business card.</div>
            </div>
            <div className="stat-card s4">
              <div className="stat-icon">{Icons.briefcase}</div>
              <div className="stat-num">35<span style={{ fontSize: 32, color: 'var(--muted)' }}>%</span></div>
              <div className="stat-label">Sales increase attributed to the act of exchanging business cards at events.</div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ FEATURES BENTO GRID ═══════ */}
      <section className="features" id="features">
        <div className="container">
          {/* TWEAK #1: No section eyebrow */}
          <h2 className="section-title">Everything you need. <em>Nothing</em> you don&rsquo;t.</h2>
          <p className="section-sub">No premium tier, no upsell, no algorithm fighting for your attention. Just a polished card, a quiet inbox, and a contact list that actually belongs to you.</p>

          <div className="features-grid">
            {/* Feature 1: Profile */}
            <div className="feature f1">
              <div className="f-num">01 &middot; Branded</div>
              <h3 className="f-title">A profile that feels like you, not like a template.</h3>
              <p className="f-desc">Headshot, company logo, tagline, bio. Built-in zoom and crop. Falls back to elegant initials when you skip the photo.</p>
              <div className="demo-card">
                <div className="demo-avatar">IE</div>
                <div className="demo-info">
                  <strong>Iwin Ettumanukaran</strong>
                  <span>Co-Founder &middot; Bite Binary</span>
                  <span className="tag">Building Connekted.in</span>
                </div>
              </div>
            </div>

            {/* Feature 2: Social toggles */}
            <div className="feature f2">
              <div className="f-num">02 &middot; Granular</div>
              <h3 className="f-title">Social links with one-tap toggles.</h3>
              <p className="f-desc">Show LinkedIn at the summit, swap to Instagram at the dinner.</p>
              <div className="toggle-list">
                <div className="landing-toggle-row">
                  <div className="label-l"><div className="icn linkedin">in</div> LinkedIn</div>
                  <div className="switch on"></div>
                </div>
                <div className="landing-toggle-row">
                  <div className="label-l"><div className="icn x">&Xopf;</div> Twitter / X</div>
                  <div className="switch on"></div>
                </div>
                <div className="landing-toggle-row">
                  <div className="label-l"><div className="icn ig">&#9673;</div> Instagram</div>
                  <div className="switch"></div>
                </div>
              </div>
            </div>

            {/* Feature 3: Wallet */}
            <div className="feature f3">
              <div className="f-num">03 &middot; Native</div>
              <h3 className="f-title">Lives in Apple Wallet &amp; Google Wallet.</h3>
              <p className="f-desc">Right next to your boarding pass and credit cards. No app to install. Pull it up, show the QR, done.</p>
              <div className="wallet-stack">
                <div className="mini-wallet apple">
                  <div className="mw-row">
                    <div className="mw-brand">Connekted.in</div>
                    <div className="mw-tag">Apple Wallet</div>
                  </div>
                  <div className="mw-name">Iwin E.</div>
                  <div className="mw-qr"></div>
                </div>
                <div className="mini-wallet google">
                  <div className="mw-row">
                    <div className="mw-brand">Connekted.in</div>
                    <div className="mw-tag">G Wallet</div>
                  </div>
                  <div className="mw-name">Iwin E.</div>
                  <div className="mw-qr"></div>
                </div>
              </div>
            </div>

            {/* Feature 4: Mandatory fields */}
            <div className="feature f4">
              <div className="f-num">04 &middot; Frictionless</div>
              <h3 className="f-title">Three required fields. The rest is up to you.</h3>
              <p className="f-desc">No interrogation, no profile completeness score. Get a shareable card in under two minutes.</p>
              <div className="form-mock">
                <div className="input focused">
                  <span>Full name</span>
                  <span className="req">Required</span>
                </div>
                <div className="input">
                  <span>Phone number</span>
                  <span className="req">Required</span>
                </div>
                <div className="input">
                  <span>Email address</span>
                  <span className="req">Required</span>
                </div>
                <div className="input">
                  <span>Title, company, bio, socials&hellip;</span>
                  <span className="opt">Optional</span>
                </div>
              </div>
            </div>

            {/* Feature 5: CRM */}
            <div className="feature f5">
              <div className="f-num">05 &middot; A real CRM</div>
              <h3 className="f-title">Every scan becomes a contact. Notes, lists, follow-ups.</h3>
              <p className="f-desc">Get a notification the moment someone scans you. Add a note, drop them in a list, follow up before they cool off.</p>
              <div className="crm-lists">
                <div className="crm-row hot"><div className="ll"><span className="dot"></span> Hot Leads</div><div className="count">12 contacts</div></div>
                <div className="crm-row warm"><div className="ll"><span className="dot"></span> Warm</div><div className="count">38 contacts</div></div>
                <div className="crm-row new"><div className="ll"><span className="dot"></span> New Scans</div><div className="count">+ 6 today</div></div>
              </div>
            </div>

            {/* Feature 6: PWA */}
            <div className="feature f6">
              <div className="f-num">06 &middot; No app store</div>
              <h3 className="f-title">A real app, installed from the web.</h3>
              <p className="f-desc">PWA with add-to-home-screen. Full-screen, app icon, instant launch. Or just use the browser. Your call.</p>
              <div className="pwa-mock">
                <div className="pwa-icon"><AscendingK size={24} color="#0A2540" /></div>
                <div className="pwa-text">
                  <strong>Add Connekted.in to Home</strong>
                  <span>Open in one tap, like an app</span>
                </div>
                <div className="pwa-btn">Install</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ QR SHOWCASE — TWEAK #3: No Q-Art mention, reframed as "your personal code" ═══════ */}
      <section className="qr-section" id="qr">
        <div className="container">
          <div className="qr-wrap">
            <div className="qr-inner">
              <div>
                <div className="section-eyebrow"><span className="dot"></span> Your personal code</div>
                <h2>A QR code that actually <em>looks like you</em>.</h2>
                <p>Your profile photo or company logo becomes part of the QR pattern. Every code is unique, branded, and unmistakably yours. Scan reliability stays perfect.</p>
                <div className="qr-feats">
                  <div className="qr-feat"><div className="chk">&#10003;</div> Your face or logo visible through the pattern</div>
                  <div className="qr-feat"><div className="chk">&#10003;</div> Colours auto-matched to your brand</div>
                  <div className="qr-feat"><div className="chk">&#10003;</div> Built-in error correction for reliable scanning</div>
                </div>
              </div>
              <div className="qr-showcase">
                <div className="qart-card">
                  <div className="qart-img">
                    <div className="finder-bl"></div>
                    <div className="qart-dots"></div>
                    <div className="qart-face">IE</div>
                    <div className="qart-watermark"><AscendingK size={16} color="#0A2540" /></div>
                  </div>
                  <div className="qart-foot">
                    <div className="qart-name">Iwin Ettumanukaran</div>
                    <div className="qart-tag">/iwin</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ HOW IT WORKS ═══════ */}
      <section className="how" id="how">
        <div className="container">
          {/* TWEAK #1: No section eyebrow */}
          <h2 className="section-title">Three steps. <em>Two minutes.</em> One card you&rsquo;ll actually want to share.</h2>

          <div className="steps">
            <div className="step">
              <div className="step-num">1</div>
              <h3>Fill in three fields</h3>
              <p>Name, phone, email. Everything else &mdash; photo, logo, socials, bio &mdash; is optional. Add them when you have time.</p>
              <div className="step-time">~ 90 seconds</div>
            </div>
            <div className="step">
              <div className="step-num">2</div>
              <h3>Download your wallet pass</h3>
              <p>One tap to Apple Wallet or Google Wallet. Your personal QR code is generated, branded, and ready to share.</p>
              <div className="step-time">~ 15 seconds</div>
            </div>
            <div className="step">
              <div className="step-num">3</div>
              <h3>Scan. Save. Follow up.</h3>
              <p>Every scan lands in your dashboard. Add notes, drop into lists, never lose a connection at a conference again.</p>
              <div className="step-time">Ongoing</div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ FOUNDER QUOTE — TWEAK #8 & #9 ═══════ */}
      <section className="quote">
        <div className="container">
          <div className="quote-wrap">
            <div className="quote-text">
              <span className="quote-mark">&ldquo;</span>
              I&rsquo;d just spent three days at an incredible summit &mdash; met investors, engineers, founders building things I&rsquo;d never even imagined. And by the time I landed home, half those conversations had <em>already slipped away</em>. Crumpled cards, forgotten names. I thought: there has to be a better handshake than paper.
            </div>
            <div className="quote-author">
              <div className="av">IE</div>
              <div className="meta">
                <strong>Iwin Ettumanukaran</strong>
                <span>Co-Founder &amp; CEO &middot; Bite Binary</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ CTA ═══════ */}
      <section className="cta" id="cta">
        <div className="container">
          <div className="cta-wrap">
            <h2>Make your first card. <em>Free, forever.</em></h2>
            <p>No credit card, no app store, no upsell. Just your card &mdash; and a quieter way to network.</p>
            <div className="cta-actions">
              <Link to="/auth" className="btn-primary">
                Claim your username
                <span className="arrow">&rarr;</span>
              </Link>
              <a href="#story" className="btn-ghost">Read our story</a>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ FOOTER — TWEAK #6 & #7 ═══════ */}
      <footer className="footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-brand">
              <div className="nav-logo" style={{ marginBottom: 16 }}>
                <AscendingK size={18} color="#0A2540" />
                <span className="nav-wordmark">Connekted <em>in</em></span>
              </div>
              <p>Digital business cards. Professional networking. Community first. Built in Milan, distributed at summits worldwide.</p>
            </div>
            <div className="footer-col">
              <h4>Product</h4>
              <ul>
                <li><a href="#features">Features</a></li>
                {/* TWEAK #6: Q-Art Codes and Roadmap removed */}
                <li><a href="#how">How it works</a></li>
                <li><a href="#qr">Your personal code</a></li>
              </ul>
            </div>
            <div className="footer-col">
              <h4>Company</h4>
              <ul>
                {/* TWEAK #4: About Bite Binary → external link */}
                <li><a href="https://bitebinary.com/#about" target="_blank" rel="noopener noreferrer">About Bite Binary</a></li>
                <li><a href="mailto:hello@connektedin.com">Contact</a></li>
              </ul>
            </div>
            <div className="footer-col">
              <h4>Legal</h4>
              <ul>
                <li><Link to="/privacy">Privacy</Link></li>
                <li><Link to="/terms">Terms</Link></li>
                <li><Link to="/cookies">Cookie policy</Link></li>
                <li><Link to="/security">Security</Link></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <div>&copy; {new Date().getFullYear()} Bite Binary &middot; Connekted.in &middot; All rights reserved</div>
            <div className="built"><span className="pulse"></span> Pre-launch</div>
          </div>
        </div>
      </footer>
    </>
  );
}
